/*
 * Your own Wealthoak invoices, on the Wallet page. A partner who is also a
 * Wealthoak client can pay an unpaid invoice on their own subscription from
 * the wallet instead of withdrawing to the bank (GET /own-invoices). Nothing
 * is paid unless they press "Pay from wallet" on an invoice and confirm.
 *
 * Shown only when the partner's own subscription was found. A partner who
 * isn't a client, or a backend where it isn't switched on, sees nothing here;
 * Settings explains how it works.
 */

import { useState } from "react";
import { ownInvoices, type OwnInvoice, type Wallet, type Withdrawal } from "../api/client";
import { useLoad } from "../api/useLoad";
import { useMe } from "../auth/AuthContext";
import { LoadError, SkeletonRows, Status, type Notify } from "./bits";
import { fmtDate, inr, toPaise } from "./format";
import { Info, Receipt } from "./icons";
import PayInvoiceDialog from "./PayInvoiceDialog";

export default function OwnInvoicesCard({
  wallet,
  notify,
  onPaid,
}: {
  wallet: Wallet | null;
  notify: Notify;
  onPaid: () => void;
}) {
  const me = useMe();
  const list = useLoad(() => ownInvoices.list());
  const [paying, setPaying] = useState<OwnInvoice | null>(null);

  // Nothing to show for a partner who isn't a client, or before it's switched on.
  if (list.data && list.data.status !== "linked") return null;

  const blocked: string | null =
    me.status !== "active"
      ? "Paying from your wallet opens once Wealthoak approves your partner account."
      : wallet && toPaise(wallet.available) <= 0
        ? "Nothing is available in your wallet yet."
        : null;

  function done(w: Withdrawal) {
    setPaying(null);
    notify(
      w.status === "paid"
        ? `${inr(w.net_amount)} paid on invoice ${w.invoice_number} from your wallet.`
        : `Your payment on invoice ${w.invoice_number} is going through. It shows as applied once Wealthoak's billing confirms it.`,
    );
    list.reload();
    onPaid();
  }

  return (
    <section className="card card--pad own-inv" aria-labelledby="own-inv-title" aria-busy={list.loading}>
      <div className="card-heading">
        <div>
          <p className="eyebrow">Your own subscription</p>
          <h2 id="own-inv-title">Pay your invoices from your wallet</h2>
        </div>
      </div>
      <p className="own-inv__lead">
        You&apos;re a Wealthoak client too, so you can pay an unpaid invoice with your commission instead of
        withdrawing it. GST and TDS come off exactly as they do for a withdrawal.
      </p>

      {list.error ? (
        <LoadError message={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <SkeletonRows rows={2} label="Loading your invoices" />
      ) : list.data.items.length === 0 ? (
        <p className="own-inv__empty">
          <Receipt size={16} aria-hidden="true" /> Nothing to pay right now. Unpaid invoices on your subscription show up
          here.
        </p>
      ) : (
        <>
          <ul className="own-inv__list">
            {list.data.items.map((inv) => (
              <li key={inv.id} className="own-inv__item">
                <span className="own-inv__what">
                  <strong className="mono">{inv.number}</strong>
                  <small>
                    {inv.date ? `Issued ${fmtDate(inv.date)}` : "Issued"}
                    {inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""}
                  </small>
                </span>
                <span className="own-inv__owed">
                  <strong className="num">{inr(inv.balance)}</strong>
                  <small>{inv.balance === inv.total ? "to pay" : `left of ${inr(inv.total)}`}</small>
                </span>
                <span className="own-inv__act">
                  {inv.in_progress ? (
                    <Status tone="amber">Processing</Status>
                  ) : (
                    <button
                      type="button"
                      className="button button--secondary button--sm"
                      disabled={Boolean(blocked) || !wallet || toPaise(inv.max_from_wallet) <= 0}
                      aria-describedby={blocked ? "own-inv-blocked" : undefined}
                      onClick={() => setPaying(inv)}
                    >
                      Pay from wallet
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {blocked && (
            <p id="own-inv-blocked" className="wallet-hero__why">
              <Info size={14} />
              {blocked}
            </p>
          )}
        </>
      )}

      {paying && wallet && (
        <PayInvoiceDialog invoice={paying} wallet={wallet} onClose={() => setPaying(null)} onDone={done} />
      )}
    </section>
  );
}
